const DEFAULT_API_BASE_URL =
  process.env.NODE_ENV === "production"
    ? "https://photo-x-yash-bhai.onrender.com/api/v1"
    : "http://localhost:4100/api/v1";
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/$/, "");

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function request(path, { method = "GET", body, signal } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    signal,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    throw new ApiError(
      payload?.error?.message ||
        `Request failed with status ${response.status}`,
      response.status,
      payload?.error?.code,
    );
  }
  return payload;
}

export async function requestOtp(email) {
  const payload = await request("/auth/otp/request", {
    method: "POST",
    body: {
      email,
      audience: "user",
      ...(globalThis.location?.href
        ? { returnUrl: globalThis.location.href }
        : {}),
    },
  });
  return payload.data;
}

export async function verifyOtp(email, code) {
  const payload = await request("/auth/otp/verify", {
    method: "POST",
    body: { email, code, audience: "user" },
  });
  return payload.data;
}

export async function exchangeAuthHandoff(token) {
  const payload = await request("/auth/handoff/exchange", {
    method: "POST",
    body: { token, workspace: "photographer" },
  });
  return payload.data;
}

export async function signOut() {
  return request("/auth/logout", { method: "POST" });
}

export async function loadPhotographerApplication(signal) {
  const payload = await request("/photographer/profile", { signal });
  return payload.data;
}

export async function savePhotographerApplication(values) {
  const payload = await request("/photographer/profile", {
    method: "PUT",
    body: values,
  });
  return payload.data;
}

export async function submitPhotographerApplication() {
  const payload = await request("/photographer/profile/submit", {
    method: "POST",
    body: { declarationAccepted: true },
  });
  return payload.data;
}

export async function submitPublicApplication(values) {
  const payload = await request("/photographer/apply", {
    method: "POST",
    body: values,
  });
  return payload.data;
}

export async function loadWorkspace(signal) {
  const [account, events, memberships] = await Promise.all([
    request("/account/me", { signal }),
    request("/events?limit=100&sort=newest&scope=photographer", { signal }),
    request("/account/memberships", { signal }),
  ]);
  return {
    user: account.data.user,
    events: events.data,
    memberships: memberships.data,
  };
}

export async function loadGallery(eventId, scope = "visible", signal) {
  const gallery = await request(
    `/events/${eventId}/gallery?scope=${scope}&limit=100`,
    { signal },
  );
  const photos = await Promise.all(
    gallery.data.map(async (photo) => {
      if (
        !photo.preview?.available &&
        !photo.thumbnail?.available &&
        !photo.original?.available
      )
        return { ...photo, mediaUrl: null };
      try {
        const media = await request(
          `/events/${eventId}/photos/${photo.id}/media?variant=${photo.preview?.available ? "preview" : "original"}`,
          { signal },
        );
        return { ...photo, mediaUrl: media.data.delivery.url };
      } catch {
        return { ...photo, mediaUrl: null };
      }
    }),
  );
  return photos;
}

export async function setFavorite(eventId, photoId, favorite) {
  return request(`/events/${eventId}/favorites/${photoId}`, {
    method: favorite ? "PUT" : "DELETE",
  });
}

export async function setSharing(eventId, photoId, shared) {
  return request(
    `/events/${eventId}/photos/${photoId}/${shared ? "share" : "unshare"}`,
    { method: "POST" },
  );
}

export async function rotateEventAccess(eventId) {
  const payload = await request(`/events/${eventId}/access/rotate`, {
    method: "POST",
    body: {},
  });
  return payload.data.access;
}

export async function uploadPhoto(eventId, file, kind) {
  const initiated = await request(`/events/${eventId}/uploads`, {
    method: "POST",
    body: {
      kind,
      originalFilename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      mode: "single",
    },
  });
  const upload = initiated.data.upload;
  const uploadResponse = await fetch(upload.url, {
    method: upload.method || "PUT",
    headers: upload.headers || { "Content-Type": file.type },
    body: file,
  });
  if (!uploadResponse.ok)
    throw new ApiError(
      "Object storage upload failed",
      uploadResponse.status,
      "STORAGE_UPLOAD_FAILED",
    );
  await request(
    `/events/${eventId}/uploads/${initiated.data.photo.id}/complete`,
    { method: "POST", body: {} },
  );
  return initiated.data.photo;
}

export async function faceSearch(eventId, selfieBase64) {
  const payload = await request(`/events/${eventId}/face-search`, {
    method: "POST",
    body: { selfieBase64, consentConfirmed: true },
  });
  return payload.data.matches;
}

export { API_BASE_URL };

// Media operations share the same event-scoped contracts as the Admin panel.
export const photographerMediaApi = {
  createBulk: async (eventId, files, idempotencyKey) =>
    (
      await request(`/events/${eventId}/bulk-uploads`, {
        method: "POST",
        body: { files, idempotencyKey },
      })
    ).data,
  initiate: async (eventId, metadata) =>
    (
      await request(`/events/${eventId}/uploads`, {
        method: "POST",
        body: metadata,
      })
    ).data,
  signPart: async (eventId, photoId, partNumber, signal) =>
    (
      await request(`/events/${eventId}/uploads/${photoId}/parts`, {
        method: "POST",
        body: { partNumber },
        signal,
      })
    ).data.part,
  complete: async (eventId, photoId, body, signal) =>
    (
      await request(`/events/${eventId}/uploads/${photoId}/complete`, {
        method: "POST",
        body,
        signal,
      })
    ).data,
  abort: async (eventId, photoId) =>
    (
      await request(`/events/${eventId}/uploads/${photoId}`, {
        method: "DELETE",
      })
    ).data,
  importDrive: async (eventId, values) =>
    (
      await request(`/events/${eventId}/drive-imports`, {
        method: "POST",
        body: values,
      })
    ).data.session,
  jobStatus: async (eventId, jobId, type) =>
    (
      await request(
        `/events/${eventId}/${type === "drive" ? "drive-imports" : "bulk-uploads"}/${jobId}`,
      )
    ).data.session,
};

export async function getPhotographerPhotoDelivery(
  eventId,
  photoId,
  variant = "original",
) {
  return (
    await request(
      `/events/${eventId}/photos/${photoId}/media?variant=${variant}`,
    )
  ).data.delivery;
}

export async function registerForEvent(eventId, message = "") {
  const result = await request(
    `/events/${encodeURIComponent(eventId)}/photographer-applications`,
    { method: "POST", body: { ...(message ? { message } : {}) } },
  );
  return result.data.application;
}
